import AdjustIcon from '@mui/icons-material/Adjust';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BarChartIcon from '@mui/icons-material/BarChart';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import BuildIcon from '@mui/icons-material/Build';
import ChatIcon from '@mui/icons-material/Chat';
import Crop54Icon from '@mui/icons-material/Crop54';
import EmailIcon from '@mui/icons-material/Email';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PrintIcon from '@mui/icons-material/Print';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WifiIcon from '@mui/icons-material/Wifi';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import {isArray} from 'lodash';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useConfiguration} from '../../../hooks/configuration';
import {useGetCommtracNodeBatteryPercentage} from '../../../hooks/get-commtrac-node-battery-percentage';
import {useGetCommtracNodeType} from '../../../hooks/get-commtrac-node-type';
import {usePrint} from '../../../hooks/print';
import {useAppSelector} from '../../../hooks/redux';
import usePrevious from '../../../hooks/usePrevious';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {BroadcastMessage} from '../../../interfaces/BroadcastMessage';
import {
  CommtracNode,
  CommtracNodeListQuery,
  CommtracNodeListResponse,
} from '../../../interfaces/CommtracNode';
import {ExportField} from '../../../interfaces/Export';
import {MapCountByLayer} from '../../../interfaces/Map';
import reduxSelectors from '../../../redux/selectors';
import {EmployeeEmergencyResponse} from '../../../utils/commtrac-nodes';
import {
  OpenedEntityMode,
  OpenedEntityType,
} from '../../../utils/connect-view-panel';
import {eventIconsLegacy} from '../../../utils/event-icons';
import {
  PRINT_HIDDEN_SX,
  PRINTABLE_CONTAINER_SX,
  PRINTABLE_MAP_SX,
} from '../../../utils/printStyles';
import AccessControl from '../../common/AccessControl';
import {getHasPermission} from '../../common/AccessControl/utils';
import {AutoRefreshSettingsSelect} from '../../common/AutoRefreshSettingsSelect';
import {BatteryIcon} from '../../common/BatteryIcon';
import {
  DataGridColumn,
  DataGridRef,
  DataGridSortBy,
} from '../../common/DataGrid';
import {Map, MapLatLangCoordinates, MapLayerId} from '../../common/Map';
import {ResizableColumns} from '../../common/ResizableColumns';
import CommtracNodeItemsEditButton from '../../commtrac-nodes/buttons/CommtracNodeItemsEditButton';
import CommtracNodeItemsPurgeButton from '../../commtrac-nodes/buttons/CommtracNodeItemsPurgeButton';
import CommtracNodeItemsUpdateButton from '../../commtrac-nodes/buttons/CommtracNodeItemsUpdateButton';
import CommtracNodeBroadcastReportFrequencyButton from '../../commtrac-nodes/CommtracNodeBroadcastReportFrequencyButton';
import CommtracNodeImportExportButton from '../../commtrac-nodes/CommtracNodeImportExportButton';
import CommtracNodeItemAssetAcknowledgeButton from '../../commtrac-nodes/CommtracNodeItemAssetAcknowledgeButton';
import CommtracNodeItemMinerAcknowledgeButton from '../../commtrac-nodes/CommtracNodeItemMinerAcknowledgeButton';
import {DateTimeSelect} from '../../selectors/DateTimeSelect';
import {ZonesSelect} from '../../selectors/ZonesSelect';
import ReportDataGrid from './ReportDataGrid';

interface Props {
  value?: CommtracNodesReportData;
  valueLive?: CommtracNodesReportDataLive;
  fetchedData?: CommtracNodeListResponse;
  countByLayer?: MapCountByLayer[];
  loading?: boolean;
  selectedBroadcastMessage?: BroadcastMessage;
  additionalParams?: Partial<CommtracNodeListQuery>;
  onChange?: (value?: CommtracNodesReportData) => void;
  onChangeLive?: (value?: CommtracNodesReportDataLive) => void;
  onOpenHistory?: (
    id: number | string,
    type:
      | 'asset'
      | 'cn'
      | 'wifi'
      | 'wifiLongTerm'
      | 'employee'
      | 'commtracNodeByCn'
      | 'networkDiagnostics'
      | 'alarm'
      | 'alarm_log'
      | 'hazard_ai_detection_log'
      | 'hazard_ai_heatmap'
      | 'amsShortTerm'
      | 'amsLongTerm'
      | 'amsLocation'
      | 'amsEmoduleInstallationHistory'
      | 'amsEmoduleSensorHistory'
      | 'amsEmoduleCalibration'
      | 'beltLocationHistoryReport'
      | 'beltHistoryReport'
  ) => void;
  onOpenItem?: (
    id: number,
    entity: OpenedEntityType,
    mode?: OpenedEntityMode
  ) => void;
  onRefresh?: () => void;
  onChangeLocationCoordinates?: (value?: MapLatLangCoordinates) => void;
  onChangeIsShownGroupChat?: (value: boolean) => void;
}

export interface CommtracNodesReportData {
  mapLayers: MapLayerId[];
  mapLevel: number | null;
  params: Partial<CommtracNodeListQuery>;
  selectedIds: number[] | null;
  shownFields: {
    all?: string[];
    miners?: string[];
    assets?: string[];
  };
  grid: {
    pageSize: number;
    page: number;
  };
  exportFields: ExportField[];
  columnWidths: Record<string, number>;
  columnFilters: Record<string, string>;
  columnOrder?: string[];
  sortBy?: DataGridSortBy | null;
}

export interface CommtracNodesReportDataExportFields {
  exportFields: ExportField[];
  params?: Partial<CommtracNodeListQuery>;
}

export interface CommtracNodesReportDataLive {
  mode: 'live' | 'snapshot';
  snapshotDatetime?: string;
}

const DEFAULT_SHOWN_FIELDS_MINERS = [
  'chat',
  'battery',
  'alarm',
  'no_location',
  'motion',
  'new_messages',
  'msg_conf',
  'emergency_response',
  'id',
  'type',
  'type_2',
  'commtrac_external_id',
  'name',
  'h_first_name',
  'h_last_name',
  'h_personal_id',
  'h_type_id',
  'commtrac_current_zone_id',
  'commtrac_strongest_cn_name',
  'commtrac_date',
  'commtrac_on_surface',
];

const DEFAULT_SHOWN_FIELDS_ASSETS = [
  'battery',
  'no_location',
  'id',
  'type',
  'type_2',
  'commtrac_external_id',
  'name',
  'm_type_id',
  'commtrac_current_zone_id',
  'commtrac_strongest_cn_name',
  'commtrac_date',
  'commtrac_on_surface',
];

const DEFAULT_SHOWN_FIELDS = union(
  DEFAULT_SHOWN_FIELDS_MINERS,
  DEFAULT_SHOWN_FIELDS_ASSETS
);

export const getCommtracNodesReportData = (): CommtracNodesReportData => {
  return {
    mapLayers: ['employees', 'assets', 'mine'],
    mapLevel: null,
    params: {
      miner_status: 'checked_in',
      asset_status: 'checked_in',
      node_type: [],
      section_ids: [],
      limit: 5000,
      page: 0,
    },
    selectedIds: null,
    shownFields: {
      all: DEFAULT_SHOWN_FIELDS,
      miners: DEFAULT_SHOWN_FIELDS_MINERS,
      assets: DEFAULT_SHOWN_FIELDS_ASSETS,
    },
    grid: {
      page: 0,
      pageSize: 25,
    },
    exportFields: [],
    columnWidths: {},
    columnFilters: {},
    columnOrder: undefined,
    sortBy: null,
  };
};

export const getCommtracNodesReportDataLive =
  (): CommtracNodesReportDataLive => {
    return {
      mode: 'live',
    };
  };

const CommtracNodesReport = ({
  value,
  valueLive,
  fetchedData,
  countByLayer,
  loading,
  selectedBroadcastMessage,
  additionalParams,
  onChange,
  onChangeLive,
  onOpenItem,
  onOpenHistory,
  onRefresh,
  onChangeLocationCoordinates,
  onChangeIsShownGroupChat,
}: Props) => {
  const renderCount = useRef(0);
  renderCount.current += 1;

  const me = useAppSelector(({app}) => app.me);
  const config = useMemo(() => value ?? getCommtracNodesReportData(), [value]);
  const configLive = useMemo(
    () => valueLive ?? getCommtracNodesReportDataLive(),
    [valueLive]
  );

  const isAssetsOnly = useMemo(
    () => isEqual(config.params.node_type, ['asset']),
    [config.params.node_type]
  );

  const isEmployeesOnly = useMemo(
    () => isEqual(config.params.node_type, ['miner']),
    [config.params.node_type]
  );

  const [liveModeMenuAnchor, setLiveModeMenuAnchor] =
    useState<null | HTMLElement>(null);

  /*************/
  /* data grid */
  /*************/
  const myPermissions = useAppSelector(({app}) => app.me?.permissions);
  const assetHumanTypes = useAppSelector(
    ({assets}) => assets.asset_human_types
  );
  const minerAddressMask = useAppSelector(
    ({assets}) => assets.constants?.miner.address_mask
  );
  const machineTypes = useAppSelector(({assets}) => assets.machine_types);
  const zones = useAppSelector(({assets}) => assets.zones);
  const shifts = useAppSelector(({assets}) => assets.shifts);
  const layers = useAppSelector(({assets}) => assets.layers);
  const getCommtracNodeType = useGetCommtracNodeType();
  const getCommtracNodeBatteryPercentage =
    useGetCommtracNodeBatteryPercentage();

  const isCompactMode = useAppSelector(reduxSelectors.app.getIsCompactMode);
  const dataGridRef = useRef<DataGridRef>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [printReport] = usePrint(printContainerRef.current);
  const lastMapLevelRef = useRef<number | null>(null);
  const rows = fetchedData?.items ?? [];

  const columns = useMemo((): DataGridColumn<CommtracNode>[] => {
    return [
      {
        field: 'select',
        type: 'select',
        doNotExport: true,
        renderHeader: () => (
          <Box sx={{display: 'flex', justifyContent: 'center'}}>
            <Checkbox
              color="primary"
              disabled={rows.length === 0}
              checked={selectedItems.length > 0 && selectedAll}
              indeterminate={selectedItems.length > 0 && !selectedAll}
              onChange={() => toggleSelectAllItems()}
            />
          </Box>
        ),
        renderCell: ({row, visibleRows}) => (
          <Box sx={{display: 'flex', justifyContent: 'center'}}>
            <Checkbox
              color="primary"
              checked={selectedItems.includes(row.id)}
              onChange={(event) =>
                toggleSelectItem(
                  row.id,
                  isShiftClick(event),
                  visibleRows.map((visibleRow) => visibleRow.id)
                )
              }
              size="small"
            />
          </Box>
        ),
      },
      {
        field: 'actions',
        renderHeader: () => (
          <Tooltip title="Edit">
            <MoreHorizIcon />
          </Tooltip>
        ),
        type: 'actions',
        doNotExport: true,
        width: 40,
        sxHeader: {textAlign: 'center', p: 0.5},
        sxCell: {textAlign: 'center', p: 0.5},
        renderCell: ({row}) => {
          if (row.type === 'miner') {
            if (row.commtrac_ack === '0' || !row.employee_asset_id) {
              return (
                <AccessControl
                  roles={[1, 2, 3]}
                  permissions={[
                    'patch::/commtrac-node/:id(\\d+)/miner/acknowledge',
                  ]}
                >
                  <CommtracNodeItemMinerAcknowledgeButton
                    item={row}
                    component={IconButton}
                    componentProps={{
                      color: 'warning',
                    }}
                    onSubmitted={(item) => {
                      onRefresh?.();
                      if (item.employee_asset_id) {
                        if (item.type === 'miner') {
                          onOpenItem?.(item.employee_asset_id, 'employee');
                        } else if (item.type === 'asset') {
                          onOpenItem?.(item.employee_asset_id, 'asset');
                        }
                      }
                    }}
                  >
                    <WarningAmberIcon />
                  </CommtracNodeItemMinerAcknowledgeButton>
                </AccessControl>
              );
            } else if (row.employee_asset_id) {
              return (
                <CommtracNodeItemsEditButton
                  component={IconButton}
                  componentProps={{
                    color: 'primary',
                    size: 'small',
                  }}
                  item={row}
                  onOpenItem={onOpenItem}
                  onOpenHistory={onOpenHistory}
                >
                  <MoreHorizIcon />
                </CommtracNodeItemsEditButton>
              );
            }
          } else if (row.type === 'asset') {
            if (row.commtrac_ack === '0' || !row.employee_asset_id) {
              return (
                <AccessControl
                  roles={[1, 2, 3]}
                  permissions={[
                    'patch::/commtrac-node/:id(\\d+)/asset/acknowledge',
                  ]}
                >
                  <CommtracNodeItemAssetAcknowledgeButton
                    item={row}
                    component={IconButton}
                    componentProps={{
                      color: 'warning',
                      size: 'small',
                    }}
                    onSubmitted={(item) => {
                      onRefresh?.();
                      if (item.employee_asset_id) {
                        onOpenItem?.(item.employee_asset_id, 'asset');
                      }
                    }}
                  >
                    <WarningAmberIcon />
                  </CommtracNodeItemAssetAcknowledgeButton>
                </AccessControl>
              );
            } else if (row.employee_asset_id) {
              return (
                <CommtracNodeItemsEditButton
                  component={IconButton}
                  componentProps={{
                    color: 'primary',
                    size: 'small',
                  }}
                  item={row}
                  onOpenItem={onOpenItem}
                  onOpenHistory={onOpenHistory}
                >
                  <MoreHorizIcon />
                </CommtracNodeItemsEditButton>
              );
            }
          }
        },
      },
      {
        field: 'chat',
        hidden:
          !myPermissions?.includes('get::/commtrac-node/:id(\\d+)/message') ||
          isAssetsOnly,
        headerName: 'Chat',
        doNotExport: true,
        width: 60,
        type: 'actions',
        sxHeader: {textAlign: 'center', p: 0.5},
        sxCell: {textAlign: 'center', p: 0.5},
        renderCell: ({row}) => {
          if (row.type === 'miner' && !row.wifi_enabled) {
            return (
              <IconButton
                color="primary"
                disabled={!row.employee_asset_id}
                onClick={() => {
                  if (row.employee_asset_id) {
                    onOpenItem?.(row.employee_asset_id, 'employee', 'chat');
                  }
                }}
                size="small"
              >
                <ChatIcon />
              </IconButton>
            );
          }
        },
      },
      {
        field: 'battery',
        headerName: 'Battery',
        width: 40,
        renderHeader: () => (
          <Tooltip title="Low Battery">
            <BatteryChargingFullIcon />
          </Tooltip>
        ),
        sortable: true,
        valueGetter: ({row}) => getCommtracNodeBatteryPercentage(row),
        renderCell: ({value}) => {
          if (typeof value === 'number') {
            return (
              <Tooltip title={`${value}%`}>
                <Box sx={{position: 'relative', left: 4, top: 4}}>
                  <BatteryIcon value={value} />
                </Box>
              </Tooltip>
            );
          } else {
            return (
              <Tooltip title="Unknown">
                <Box sx={{position: 'relative', left: 4, top: 4}}>
                  <QuestionMarkIcon />
                </Box>
              </Tooltip>
            );
          }
        },
      },
      {
        field: 'alarm',
        headerName: 'Alarm',
        hidden: isAssetsOnly,
        width: 40,
        renderHeader: () => (
          <Tooltip title="Alarm">
            <ReportProblemIcon />
          </Tooltip>
        ),
        sortable: true,
        valueGetter: ({row}) => !!row.e_201,
        renderCell: ({row}) => {
          if (row.type === 'miner') {
            if (row.e_201) {
              return (
                <Tooltip title="Employee Alarm">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.alarm}
                  </Box>
                </Tooltip>
              );
            }
          }
        },
      },
      {
        field: 'no_location',
        headerName: 'No Location',
        width: 40,
        renderHeader: () => (
          <Tooltip title="No Location">
            <WifiIcon />
          </Tooltip>
        ),
        sortable: true,
        valueGetter: ({row}) => {
          if (row.e_202 || row.e_301) {
            return 'no';
          } else if (row.e_221 || row.e_321) {
            return 'many';
          } else if (row.e_226 || row.e_326) {
            return 'invalid';
          }
        },
        renderCell: ({row}) => {
          if (row.type === 'miner') {
            if (row.e_221) {
              return (
                <Tooltip title="Many Location">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.manyLocation}
                  </Box>
                </Tooltip>
              );
            } else if (row.e_202) {
              return (
                <Tooltip title="No Location">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.noLocation}
                  </Box>
                </Tooltip>
              );
            } else if (row.e_226) {
              return (
                <Tooltip title="Location Derived">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.derivedLocation}
                  </Box>
                </Tooltip>
              );
            }
          } else if (row.type === 'asset') {
            if (row.e_321) {
              return (
                <Tooltip title="Many Location">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.manyLocation}
                  </Box>
                </Tooltip>
              );
            } else if (row.e_301) {
              return (
                <Tooltip title="No Location">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.noLocation}
                  </Box>
                </Tooltip>
              );
            } else if (row.e_326) {
              return (
                <Tooltip title="Location Derived">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.derivedLocation}
                  </Box>
                </Tooltip>
              );
            }
          }
        },
      },
      {
        field: 'motion',
        headerName: 'Motion',
        hidden: isAssetsOnly,
        width: 40,
        renderHeader: () => (
          <Tooltip title="Motion">
            <PersonRemoveIcon />
          </Tooltip>
        ),
        sortable: true,
        valueGetter: ({row}) => (row.e_233 ? row.motion_sensor_value : -1),
        renderCell: ({row, value}) => {
          if (row.type === 'miner') {
            if (value === 0) {
              return (
                <Tooltip title="Employee is in Not Moving State (Underground)">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerNotMoving}
                  </Box>
                </Tooltip>
              );
            } else if (value === 1) {
              return (
                <Tooltip title="Employee is in Not Moving State (Surface)">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerNotMoving}
                  </Box>
                </Tooltip>
              );
            } else if (value === 2) {
              return (
                <Tooltip title="Moving State Ok (Surface)">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerOkMoving}
                  </Box>
                </Tooltip>
              );
            } else if (value === 3) {
              return (
                <Tooltip title="Moving State Ok (Underground)">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerOkMoving}
                  </Box>
                </Tooltip>
              );
            }
          }
        },
      },
      {
        field: 'new_messages',
        hidden: isAssetsOnly,
        width: 40,
        renderHeader: () => (
          <Tooltip title="New Messages">
            <EmailIcon />
          </Tooltip>
        ),
        sortable: true,
        doNotExport: true,
        valueGetter: ({row}) => !!row.e_204,
        renderCell: ({row}) => {
          if (row.type === 'miner') {
            if (row.e_204) {
              return (
                <Tooltip title="New Message">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    <EmailIcon color="success" />
                  </Box>
                </Tooltip>
              );
            }
          }
        },
      },
      {
        field: 'msg_conf',
        hidden: isAssetsOnly,
        headerName: 'Msg Conf',
        width: 40,
        renderHeader: () => (
          <Tooltip title="Msg Conf">
            <BuildIcon />
          </Tooltip>
        ),
        sortable: true,
        valueGetter: ({row}) => !!row.e_222,
        renderCell: ({row}) => {
          if (row.type === 'miner') {
            if (row.e_222) {
              return (
                <Tooltip title="Configuration Message Failed">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerMsgConfFailed}
                  </Box>
                </Tooltip>
              );
            } else if (row.configuration_message?.answer === 'NACK') {
              return (
                <Tooltip title="NACK">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerNack}
                  </Box>
                </Tooltip>
              );
            } else if (row.e_232) {
              return (
                <Tooltip title="ACK">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerAck}
                  </Box>
                </Tooltip>
              );
            }
          }
        },
      },
      {
        field: 'emergency_response',
        hidden: isAssetsOnly,
        headerName: 'Emergency Response',
        width: 40,
        renderHeader: () => (
          <Tooltip title="Emergency Response">
            <AdjustIcon color="error" />
          </Tooltip>
        ),
        sortable: true,
        doNotExport: true,
        renderCell: ({row}) => {
          if (row.type === 'miner' && selectedBroadcastMessage) {
            const message = row.broadcast_message?.find(
              (i) => i.message_id === selectedBroadcastMessage?.message_id
            );
            if (message?.answer === 'NACK') {
              return (
                <Tooltip title="Not responded">
                  <Box sx={{position: 'relative', left: 4, top: 4}}>
                    {eventIconsLegacy.commtracNode.minerAnswerNack}
                  </Box>
                </Tooltip>
              );
            } else if (message?.answer === 'ANS') {
              if (message.broadcast_status === '1') {
                return (
                  <Tooltip title={EmployeeEmergencyResponse.OK}>
                    <Box sx={{position: 'relative', left: 4, top: 4}}>
                      {
                        eventIconsLegacy.commtracNode
                          .employeeEmergencyResponseOk
                      }
                    </Box>
                  </Tooltip>
                );
              } else if (message.broadcast_status === '2') {
                return (
                  <Tooltip title={EmployeeEmergencyResponse.INJURED_BUT_OK}>
                    <Box sx={{position: 'relative', left: 4, top: 4}}>
                      {
                        eventIconsLegacy.commtracNode
                          .employeeEmergencyResponseInjuredButOk
                      }
                    </Box>
                  </Tooltip>
                );
              } else if (message.broadcast_status === '3') {
                return (
                  <Tooltip
                    title={EmployeeEmergencyResponse.INJURED_AND_NEEDS_HELP}
                  >
                    <Box sx={{position: 'relative', left: 4, top: 4}}>
                      {
                        eventIconsLegacy.commtracNode
                          .employeeEmergencyResponseInjuredAndNeedsHelp
                      }
                    </Box>
                  </Tooltip>
                );
              }
              return (
                <Tooltip title="ACK">
                  {eventIconsLegacy.commtracNode.minerAnswerAck}
                </Tooltip>
              );
            }
          }
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        sortable: true,
        filterable: true,
        filterPlaceholder: 'Filter by name...',
        width: 160,
        resizeable: true,
        valueGetter: ({row}) => {
          if (row.type === 'miner') {
            return row.h_nickname;
          } else if (row.type === 'asset') {
            return row.name;
          }
        },
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) =>
          capitalize(row.type === 'miner' ? 'Employee' : row.type),
      },
      {
        field: 'type_2',
        headerName: 'Type 2',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => getCommtracNodeType(row),
      },
      {
        hidden: isAssetsOnly,
        field: 'h_personal_id',
        headerName: 'Employee ID',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.h_personal_id ?? null,
      },
      {
        field: 'commtrac_external_id',
        headerName: 'Network ID',
        width: 100,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => {
          if (row.wifi_enabled) {
            return row.mac_address;
          } else if (row.type === 'miner' && row.commtrac_external_id) {
            return minerAddressMask // eslint-disable-next-line
              ? // eslint-disable-next-line no-bitwise
                row.commtrac_external_id & minerAddressMask
              : null;
          } else if (row.type === 'asset') {
            return row.commtrac_external_id;
          }
        },
      },
      {
        field: 'external_id',
        headerName: 'Proximity ID',
        width: 100,
        resizeable: true,
        sortable: true,
      },
      {
        hidden: isAssetsOnly,
        field: 'h_first_name',
        headerName: 'First Name',
        width: 100,
        resizeable: true,
        sortable: true,
        filterable: true,
        filterPlaceholder: 'Filter by first name...',
        valueGetter: ({row}) => row.h_first_name,
      },
      {
        hidden: isAssetsOnly,
        field: 'h_last_name',
        headerName: 'Last Name',
        width: 100,
        resizeable: true,
        sortable: true,
        filterable: true,
        filterPlaceholder: 'Filter by last name...',
        valueGetter: ({row}) => row.h_last_name,
      },
      {
        hidden: isAssetsOnly,
        field: 'h_type_id',
        headerName: 'Job Type',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) =>
          assetHumanTypes.find((i) => row.h_type_id === i.id)?.name,
      },
      {
        hidden: isEmployeesOnly,
        field: 'm_type_id',
        headerName: 'Machine Type',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) =>
          machineTypes.find((i) => row.m_type_id === i.id)?.name,
      },
      {
        field: 'commtrac_current_zone_id',
        headerName: 'Section',
        width: 120,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) =>
          zones.find((i) => i.id === row.commtrac_current_zone_id)?.name,
      },
      {
        field: 'commtrac_strongest_cn_name',
        headerName: 'Strongest Node',
        width: 120,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'commtrac_date',
        headerName: 'Timestamp',
        width: 160,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'commtrac_on_surface',
        headerName: 'Pos',
        width: 60,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => row.commtrac_on_surface === '1',
        renderCell: ({row}) => {
          if (row.commtrac_on_surface === '1') {
            return (
              <Tooltip title="Surface">
                <Box sx={{position: 'relative', left: 4, top: 4}}>
                  <BarChartIcon color="success" />
                </Box>
              </Tooltip>
            );
          } else if (row.commtrac_on_surface === '0') {
            return (
              <Tooltip title="Underground">
                <Box sx={{position: 'relative', left: 4, top: 4}}>
                  <Crop54Icon color="success" />
                </Box>
              </Tooltip>
            );
          }
        },
      },
      {
        field: 'layer_id',
        headerName: 'Layer',
        width: 80,
        sortable: true,
        resizeable: true,
        valueGetter: ({row}) =>
          layers.find((i) => i.id === row.z_layer_id)?.name,
      },
      {
        field: 'commtrac_pos_lon',
        headerName: 'Longitude',
        width: 120,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'commtrac_pos_lat',
        headerName: 'Latitude',
        width: 120,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'commtrac_report_frequency',
        headerName: 'Report Frequency',
        width: 160,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_type',
        hidden: isAssetsOnly,
        headerName: 'Sensor 1',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_value',
        hidden: isAssetsOnly,
        headerName: 'Value 1',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_type2',
        hidden: isAssetsOnly,
        headerName: 'Sensor 2',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_value2',
        hidden: isAssetsOnly,
        headerName: 'Value 2',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_type3',
        hidden: isAssetsOnly,
        headerName: 'Sensor 3',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_value3',
        hidden: isAssetsOnly,
        headerName: 'Value 3',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_type4',
        hidden: isAssetsOnly,
        headerName: 'Sensor 4',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'a_value4',
        hidden: isAssetsOnly,
        headerName: 'Value 4',
        width: 80,
        resizeable: true,
        sortable: true,
      },
      {
        field: 'h_birth_date',
        hidden: isAssetsOnly,
        headerName: 'Birthday',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) =>
          row.h_birth_date
            ? dayjs.utc(row.h_birth_date).format('YYYY-MM-DD')
            : '',
      },
      {
        field: 'h_shift_id',
        hidden: isAssetsOnly,
        headerName: 'Shift',
        width: 80,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) =>
          shifts?.find((shift) => shift.id === row.h_shift_id)?.name,
      },
      {
        field: 'commtrac_beacon_data',
        headerName: 'Beacon Data',
        sortable: true,
        width: 180,
        resizeable: true,
      },
      {
        field: 'date_activated',
        headerName: 'Activated at',
        width: 180,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => {
          if (row.type === 'miner') {
            return row.h_date_activated;
          } else if (row.type === 'asset') {
            return row.m_date_activated;
          }
        },
      },
      {
        field: 'date_deactivated',
        headerName: 'Deactivated at',
        width: 180,
        resizeable: true,
        sortable: true,
        valueGetter: ({row}) => {
          if (row.type === 'miner') {
            return row.h_date_deactivated;
          } else if (row.type === 'asset') {
            return row.m_date_deactivated;
          }
        },
      },
    ];
  }, [
    isAssetsOnly,
    isEmployeesOnly,
    myPermissions,
    selectedBroadcastMessage,
    rows.length,
    config.selectedIds,
    assetHumanTypes,
    minerAddressMask,
    machineTypes,
    zones,
    shifts,
    layers,
    getCommtracNodeType,
    getCommtracNodeBatteryPercentage,
    onOpenItem,
    onOpenHistory,
    onRefresh,
  ]);

  const shownFields = useMemo(() => {
    if (isEmployeesOnly) {
      return config.shownFields.miners;
    } else if (isAssetsOnly) {
      return config.shownFields.assets;
    }
    return config.shownFields.all;
  }, [config.shownFields]);

  const defaultShownFields = useMemo(() => {
    if (isEmployeesOnly) {
      return DEFAULT_SHOWN_FIELDS_MINERS;
    } else if (isAssetsOnly) {
      return DEFAULT_SHOWN_FIELDS_ASSETS;
    }
    return DEFAULT_SHOWN_FIELDS;
  }, [isEmployeesOnly, isAssetsOnly]);

  useEffect(() => {
    onChange?.(
      update(config, {
        exportFields: {
          $set: columns
            .filter((col) => !col.doNotExport)
            .map((col) => ({
              field: col.field,
              label: col.headerName,
              hidden: shownFields?.indexOf(col.field) === -1,
            })),
        },
      })
    );
  }, [shownFields]);

  const handleChangeShownFields = useCallback(
    (fields: string[]) => {
      if (isEmployeesOnly) {
        onChange?.(update(config, {shownFields: {miners: {$set: fields}}}));
      } else if (isAssetsOnly) {
        onChange?.(update(config, {shownFields: {assets: {$set: fields}}}));
      } else {
        onChange?.(update(config, {shownFields: {all: {$set: fields}}}));
      }
    },
    [onChange, config, isEmployeesOnly, isAssetsOnly]
  );

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = config.selectedIds ?? [];

  const selectedRows = useMemo(
    () => rows.filter((i) => config.selectedIds?.includes(i.id)),
    [rows, config.selectedIds]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<number>();

  const toggleSelectItem = (
    id: number,
    shiftKey: boolean,
    visibleIds: number[]
  ) => {
    const nextSelectedIds = getNextSelectedIds({
      id,
      visibleIds,
      selectedIds: config.selectedIds ?? [],
      shiftKey,
    });
    onChange?.(
      update(config, {
        selectedIds: {
          $set: nextSelectedIds,
        },
      })
    );
  };

  const selectAll = () => {
    onChange?.(
      update(config, {
        selectedIds: {
          $set: rows?.map((i) => i.id) ?? [],
        },
      })
    );
  };

  const unselectAll = () => {
    onChange?.(
      update(config, {
        selectedIds: {
          $set: [],
        },
      })
    );
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  useEffect(() => {
    if (
      config.selectedIds &&
      config.selectedIds.length !== selectedRows.length
    ) {
      onChange?.(
        update(config, {
          selectedIds: {
            $set: selectedRows.map((i) => i.id),
          },
        })
      );
    }
  }, [config.selectedIds, selectedRows]);

  const prevSelectedAll = usePrevious(selectedAll);

  useEffect(() => {
    if (prevSelectedAll && !selectedAll) {
      selectAll();
    }
  }, [rows]);

  useEffect(() => {
    if (lastMapLevelRef.current === config.mapLevel) {
      return;
    }
    lastMapLevelRef.current = config.mapLevel;
    onChange?.(
      update(config, {
        params: {
          section_ids: {
            $set: [],
          },
          map_level: {
            $set: config.mapLevel,
          },
        },
      })
    );
  }, [config.mapLevel]);

  /************/
  /* map data */
  /************/
  const mapData = useMemo(() => {
    if (fetchedData?.items && config.selectedIds?.length) {
      return fetchedData?.items.filter((i) =>
        config.selectedIds?.includes(i.id)
      );
    }
    return [];
  }, [fetchedData, config.selectedIds, selectedItems]);

  /********************/
  /* refresh interval */
  /********************/
  const refreshPeriod = useConfiguration(
    'auto-refresh',
    'commtrac_miner_autorefresh_rate'
  );

  const canPurge = (permissions: any) => {
    return getHasPermission(
      permissions ?? [],
      ['post::/purge'],
      me?.type_id,
      selectedRows?.length
    );
  };

  const canCheckInOut = useCallback(
    (permissions: any) => {
      return (
        getHasPermission(permissions ?? [], [
          'patch::/commtrac-node/force_check_out',
        ]) ||
        getHasPermission(permissions ?? [], [
          'patch::/commtrac-node/force_check_in',
        ]) ||
        ((getHasPermission(permissions ?? [], [
          'patch::/commtrac-node/:id(\\d+)/miner/force_check_in',
        ]) ||
          getHasPermission(permissions ?? [], [
            'patch::/commtrac-node/:id(\\d+)/miner/force_check_out',
          ]) ||
          getHasPermission(permissions ?? [], [
            'patch::/commtrac-node/:id(\\d+)/asset/force_check_in',
          ]) ||
          getHasPermission(permissions ?? [], [
            'patch::/commtrac-node/:id(\\d+)/asset/force_check_out',
          ])) &&
          selectedRows.length === 1)
      );
    },
    [selectedRows]
  );

  const allCheckedIn = useMemo(() => {
    return (
      selectedRows.filter(
        (it) =>
          it.status === 'active' && it.checked_in && it.commtrac_ack !== '0'
      ).length === selectedRows.length
    );
  }, [selectedRows]);

  const allCheckedOut = useMemo(() => {
    return (
      selectedRows.filter(
        (it) =>
          it.status === 'active' && !it.checked_in && it.commtrac_ack !== '0'
      ).length === selectedRows.length
    );
  }, [selectedRows]);

  const checkInOutPossible = useMemo(() => {
    return canCheckInOut(me?.permissions) && (allCheckedIn || allCheckedOut);
  }, [selectedRows]);

  const purgePossible = useMemo(() => {
    return canPurge(me?.permissions);
  }, [selectedRows]);

  const canShowActionButton = useMemo(() => {
    return purgePossible || checkInOutPossible;
  }, [selectedRows]);

  const handleColumnWidthChange = useCallback(
    (widths: Record<string, number>) => {
      onChange?.(update(config, {columnWidths: {$set: widths}}));
    },
    [onChange, config]
  );

  const handleColumnFiltersChange = useCallback(
    (filters: Record<string, string>) => {
      onChange?.(update(config, {columnFilters: {$set: filters}}));
    },
    [onChange, config]
  );

  const handleSortChange = useCallback(
    (sortBy?: DataGridSortBy) => {
      onChange?.(update(config, {sortBy: {$set: sortBy ?? null}}));
    },
    [onChange, config]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (isEmployeesOnly) {
        onChange?.(
          update(config, {
            columnOrder: {$set: order},
            shownFields: {miners: {$set: fields}},
          })
        );
      } else if (isAssetsOnly) {
        onChange?.(
          update(config, {
            columnOrder: {$set: order},
            shownFields: {assets: {$set: fields}},
          })
        );
      } else {
        onChange?.(
          update(config, {
            columnOrder: {$set: order},
            shownFields: {all: {$set: fields}},
          })
        );
      }
    },
    [onChange, config, isEmployeesOnly, isAssetsOnly]
  );

  const sxFooter = useMemo(
    () => ({
      bgcolor: (theme: any) =>
        theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
    }),
    []
  );

  const footerStart = useMemo(
    () =>
      selectedItems.length ? (
        <Box display="flex" alignItems="center" gap={3} px={1}>
          <Box
            display="flex"
            gap={0.5}
            alignItems="center"
            height="100%"
            whiteSpace="nowrap"
          >
            {selectedItems.length} selected
          </Box>
          {canShowActionButton && (
            <Box display="flex" height={40} gap={1}>
              <CommtracNodeItemsUpdateButton
                ids={selectedRows.map((i) => ({
                  id: i.id,
                  type: i.type,
                  wifi_enabled: i.wifi_enabled,
                  commtrac_ack: i.commtrac_ack,
                  commtrac_external_id: i.commtrac_external_id,
                  atest: i.atest,
                  mc2_flag: i.mc2_flag,
                  has_motion_sensor: i.has_motion_sensor,
                  cam_monitor_battery: i.cam_monitor_battery,
                }))}
                componentProps={{size: 'small', variant: 'outlined'}}
                onDone={() => onRefresh?.()}
              >
                Update
              </CommtracNodeItemsUpdateButton>
              <CommtracNodeItemsPurgeButton
                ids={selectedRows.map((i) => ({
                  id: i.id,
                  checked_in: i.checked_in,
                  commtrac_ack: i.commtrac_ack,
                  type: i.type,
                  status: i.status,
                  wifi_enabled: i.wifi_enabled,
                }))}
                purgeChatEnabled={selectedRows.some(
                  (i) => i.type === 'miner' && !i.wifi_enabled
                )}
                componentProps={{size: 'small', variant: 'outlined'}}
                onDone={() => onRefresh?.()}
              >
                <MoreHorizIcon />
              </CommtracNodeItemsPurgeButton>
            </Box>
          )}
        </Box>
      ) : null,
    [selectedItems, selectedRows, canShowActionButton, onRefresh]
  );

  const handlePageChange = useCallback(
    (v: number) => {
      onChange?.(update(config, {grid: {page: {$set: v}}}));
    },
    [onChange, config]
  );

  const handlePageSizeChange = useCallback(
    (v: number) => {
      onChange?.(update(config, {grid: {pageSize: {$set: v}}}));
    },
    [onChange, config]
  );

  const availableMapLayers = useMemo(
    (): MapLayerId[] => [
      'employees',
      'assets',
      'street',
      'mine',
      'nodes',
      'alarms',
    ],
    []
  );

  const onSelectMapLayers = useCallback(
    (v: MapLayerId[]) => {
      onChange?.(
        update(config, {
          mapLayers: {
            $set: v,
          },
          params: {
            node_type: {
              $set: [
                ...(v.includes('employees') ? ['miner'] : []),
                ...(v.includes('assets') ? ['asset'] : []),
              ] as CommtracNodesReportData['params']['node_type'],
            },
          },
        })
      );
    },
    [onChange, config]
  );

  const onSelectLevel = useCallback(
    (v: number | null) => {
      onChange?.(
        update(config, {
          mapLevel: {
            $set: v,
          },
        })
      );
    },
    [onChange, config]
  );

  return (
    <Box height="100%" width="100%">
      <Box ref={printContainerRef} sx={PRINTABLE_CONTAINER_SX}>
        <ResizableColumns
          left={
            <>
              <Box
                display="flex"
                alignItems="center"
                height={isCompactMode ? 56 : 70}
                p={isCompactMode ? 1 : 2}
                minWidth={520}
                gap={2}
                sx={PRINT_HIDDEN_SX}
              >
                <AccessControl permissions={['get::/group-message']}>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{height: '100%'}}
                    startIcon={<ChatIcon />}
                    onClick={() => onChangeIsShownGroupChat?.(true)}
                  >
                    Group Message
                  </Button>
                </AccessControl>
                <AccessControl
                  permissions={[
                    'patch::/commtrac-node/miner/broadcast_report_frequency',
                  ]}
                >
                  <CommtracNodeBroadcastReportFrequencyButton
                    componentProps={{
                      variant: 'outlined',
                      size: 'small',
                      sx: {height: '100%'},
                    }}
                  >
                    Broadcast Reporting Frequency
                  </CommtracNodeBroadcastReportFrequencyButton>
                </AccessControl>
              </Box>
              <Map
                panel="cn_report"
                commtracNodes={mapData}
                countByLayer={countByLayer}
                selectedMapLayers={config.mapLayers}
                selectedLevel={config.mapLevel}
                height="calc(100% - 70px)"
                minWidth={200}
                availableMapLayers={availableMapLayers}
                onSelectMapLayers={onSelectMapLayers}
                onSelectLevel={onSelectLevel}
                onOpenItem={onOpenItem}
                onOpenHistory={onOpenHistory}
                onGetClickCoordinates={onChangeLocationCoordinates}
                sx={PRINTABLE_MAP_SX}
              />
            </>
          }
        >
          <Box display="flex" flexDirection="column" height="100%">
            <Box
              py={isCompactMode ? 1 : 2}
              pr={isCompactMode ? 1 : 2}
              minWidth={600}
              sx={PRINT_HIDDEN_SX}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                height={40}
                gap={2}
              >
                <Box flexGrow={1} display="flex" height="100%" gap={2}>
                  <ZonesSelect
                    value={config.params.section_ids}
                    multiple={true}
                    layer={config.mapLevel}
                    size="small"
                    nullLabel="All Sections"
                    maxDefaultWidth={400}
                    sx={{flexShrink: 0, flexGrow: 1, maxWidth: 400}}
                    onChange={(v) => {
                      const sectionIds = isArray(v) ? v : [+v];
                      onChange?.(
                        update(config, {
                          params: {
                            section_ids: {
                              $set: sectionIds as number[],
                            },
                          },
                        })
                      );
                    }}
                  />

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      setLiveModeMenuAnchor(e.currentTarget);
                    }}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{minWidth: 180, flexShrink: 0}}
                  >
                    {configLive.mode === 'live'
                      ? 'Live'
                      : configLive.snapshotDatetime
                        ? dayjs(configLive.snapshotDatetime).format(
                            'YYYY-MM-DD HH:mm:ss'
                          )
                        : 'Snapshot'}
                  </Button>
                  <Menu
                    anchorEl={liveModeMenuAnchor}
                    open={Boolean(liveModeMenuAnchor)}
                    onClose={() => {
                      setLiveModeMenuAnchor(null);
                    }}
                  >
                    <MenuItem
                      selected={configLive.mode === 'live'}
                      onClick={() => {
                        onChangeLive?.(
                          update(configLive, {
                            mode: {$set: 'live'},
                          })
                        );
                        setLiveModeMenuAnchor(null);
                      }}
                    >
                      Live
                    </MenuItem>
                    <MenuItem
                      selected={configLive.mode === 'snapshot'}
                      sx={{flexDirection: 'column', alignItems: 'flex-start'}}
                    >
                      <DateTimeSelect
                        value={
                          configLive.snapshotDatetime
                            ? dayjs(configLive.snapshotDatetime).toDate()
                            : null
                        }
                        onChange={(v) => {
                          onChangeLive?.(
                            update(configLive, {
                              mode: {$set: 'snapshot'},
                              snapshotDatetime: {
                                $set: v
                                  ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
                                  : undefined,
                              },
                            })
                          );
                        }}
                        onAccept={() => {
                          setLiveModeMenuAnchor(null);
                          onRefresh?.();
                        }}
                      />
                    </MenuItem>
                  </Menu>
                </Box>

                <Box display="flex" height="100%">
                  <ButtonGroup>
                    <CommtracNodeImportExportButton
                      value={value}
                      additionalParams={additionalParams}
                      component={Button}
                      componentProps={{color: 'primary'}}
                      onSubmitted={() => onRefresh?.()}
                    >
                      <ImportExportIcon />
                    </CommtracNodeImportExportButton>

                    <Button onClick={() => onRefresh?.()}>
                      <Tooltip title="Refresh">
                        <RefreshIcon />
                      </Tooltip>
                    </Button>

                    {refreshPeriod && configLive.mode === 'live' ? (
                      <AutoRefreshSettingsSelect
                        refreshPeriod={refreshPeriod}
                      />
                    ) : null}

                    <Button onClick={() => printReport()}>
                      <Tooltip title="Print">
                        <PrintIcon />
                      </Tooltip>
                    </Button>
                  </ButtonGroup>
                </Box>
              </Box>
            </Box>
            <ReportDataGrid
              refObj={dataGridRef}
              rows={rows}
              columns={columns as any}
              page={config.grid.page}
              pageSize={config.grid.pageSize}
              loading={loading}
              shownFields={shownFields}
              defaultShownFields={defaultShownFields}
              columnWidths={config.columnWidths}
              columnOrder={config.columnOrder}
              columnFilters={config.columnFilters ?? {}}
              sortBy={config.sortBy ?? null}
              sxFooter={sxFooter}
              footerStart={footerStart}
              onSort={handleSortChange}
              onShownFieldsChange={handleChangeShownFields}
              onColumnWidthsChange={handleColumnWidthChange}
              onColumnOrderAndShownFieldsChange={
                handleColumnOrderAndShownFieldsChange
              }
              onColumnFiltersChange={handleColumnFiltersChange}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </Box>
        </ResizableColumns>
      </Box>
    </Box>
  );
};

export default memo(CommtracNodesReport);
